import { InfoOutline } from '@mui/icons-material';
import { Card, CardContent, Divider, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorBoundary from 'components/ErrorBoundary';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import {
  CalendarDay,
  calculateLcyValue,
  getCurrencySymbol,
  numberToCurrencyString,
} from 'further-ui/utils';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { revaluation as rules } from 'further-ui/validations';
import { sumBy } from 'lodash';
import { Controller } from 'react-hook-form';
import { RevaluationExternalState } from '../FormWrapper';
import useRevaluationsFormHandlers from '../hooks/useRevaluationFormHandlers';
import useRevaluations from '../hooks/useRevaluations';
import CurrentValuationRow from './CurrentValuationRow';
import { HoldingClassInvestmentTotal } from './types';
import { ReactNode } from 'react';

const NoPaddingCardContent = styled(CardContent)(() => ({
  padding: 0,
  '&:last-child': { paddingBottom: 0 },
}));

const TransparentCard = styled(Card)(() => ({
  backgroundColor: 'transparent',
}));

const WhiteInputsRow = styled(Row)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
  },
}));

type OverallRevaluationProps = {
  assetName: string;
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  viewOnly?: boolean;
  disableBorder?: boolean;
  isNonLcy?: boolean;
  vehicleBaseCurrency?: CurrencyCode;
  fxRate?: CurrencyRateDetails | null;
  disableDate?: boolean;
  rateSelector?: ReactNode;
};

const OverallRevaluationSection: React.FC<OverallRevaluationProps> = ({
  assetName,
  holdingClassInvestmentTotals,
  viewOnly,
  disableBorder,
  isNonLcy,
  vehicleBaseCurrency,
  fxRate,
  disableDate,
  rateSelector,
}) => {
  const { formMethods } = useFormWithStateContext<
    rules.RevaluationFormType,
    RevaluationExternalState
  >();

  const { watch, control, getValues, setValue } = formMethods;
  const { latestRevaluation } = useRevaluations({
    watch,
    control,
    getValues,
  });

  const {
    updateAllNewRevaluationsDate,
    handleOverallHoldValueChange,
    handleOverallUnitPriceChange,
    setFieldFocus,
  } = useRevaluationsFormHandlers({
    watch,
    setValue,
  });

  const holdingClassCurrency: CurrencyCode =
    holdingClassInvestmentTotals[0]?.currency;

  const assetHasMoreThanOneCurrency = holdingClassInvestmentTotals.some(
    (holdingClass) => holdingClass.currency !== holdingClassCurrency,
  );

  const numberOfUnits = sumBy(
    holdingClassInvestmentTotals,
    'currentNumberOfUnits',
  );

  const latestRevaluationDateFormatted =
    (latestRevaluation &&
      new CalendarDay(latestRevaluation.ledgerDate).toReadable()) ??
    '-';

  //These values are static as they are not affected by new revaluations
  const currentHoldValueStatic = sumBy(
    holdingClassInvestmentTotals,
    'currentHoldValue',
  );

  const averageUnitPriceStatic =
    numberOfUnits && currentHoldValueStatic
      ? currentHoldValueStatic / numberOfUnits
      : undefined;

  const averageUnitPriceStaticFormatted = !assetHasMoreThanOneCurrency
    ? numberToCurrencyString(averageUnitPriceStatic ?? 0, {
        currency: holdingClassCurrency,
        unlimitedDp: true,
      })
    : '-';

  const currentHoldValueStaticFormatted = !assetHasMoreThanOneCurrency
    ? numberToCurrencyString(currentHoldValueStatic, {
        currency: holdingClassCurrency,
      })
    : '-';

  const currentHoldValueLcyStatic = holdingClassInvestmentTotals.every(
    (hc) => hc.currentHoldValueLcy != null,
  )
    ? sumBy(holdingClassInvestmentTotals, (hc) => hc.currentHoldValueLcy ?? 0)
    : undefined;

  const currentHoldValueLcyStaticFormatted =
    isNonLcy && vehicleBaseCurrency && currentHoldValueLcyStatic !== undefined
      ? numberToCurrencyString(currentHoldValueLcyStatic, {
          currency: vehicleBaseCurrency,
        })
      : undefined;

  const newHoldValue = watch('currentHoldValue') ?? 0;
  const newUnitPrice = watch('currentUnitPrice') ?? 0;
  const newRevaluationDate = watch('currentRevaluationDate') ?? new Date();

  const newHoldValueLcy =
    isNonLcy && fxRate ? calculateLcyValue(newHoldValue, fxRate) : undefined;

  const Content = disableBorder ? NoPaddingCardContent : CardContent;
  const CardComponent = disableBorder ? TransparentCard : Card;
  const InputsRow = disableBorder ? WhiteInputsRow : Row;

  return (
    <ErrorBoundary>
      <Column spacing="lg">
        <CardComponent
          variant={disableBorder ? undefined : 'outlined'}
          elevation={disableBorder ? 0 : undefined}
        >
          <Content>
            <Column spacing="lg">
              {!disableBorder && (
                <Row centered spacing="xxs">
                  <Typography variant="h6">
                    {`Total ${assetName} asset value`}
                  </Typography>
                  <Tooltip title="Total asset revaluation should only be used when the unit price is the same across all holding classes.">
                    <InfoOutline fontSize="inherit" />
                  </Tooltip>
                </Row>
              )}
              <InputsRow centered fullWidth spacing="lg" breakpoint="md">
                <FormField
                  labelSize="xs"
                  size="md"
                  label={
                    isNonLcy
                      ? `New hold value (${getCurrencySymbol(
                          holdingClassCurrency,
                        )})`
                      : 'New hold value'
                  }
                  layout="horizontal"
                >
                  <Controller
                    name="currentHoldValue"
                    control={control}
                    render={({
                      field: { ref, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <NumberInput
                        fullWidth
                        {...rest}
                        onChange={(value) => {
                          onChange(value);
                          handleOverallHoldValueChange(
                            value ?? 0,
                            numberOfUnits,
                          );
                        }}
                        currencyStartAdornment
                        onFocus={() => setFieldFocus('holdValue')}
                        currency={holdingClassCurrency}
                        decimals={2}
                        fixedDecimalScale
                        disabled={viewOnly}
                        error={!!error}
                        helperText={error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormField>
                <FormField
                  labelSize="xs"
                  size="md"
                  label="New unit price"
                  layout="horizontal"
                >
                  <Controller
                    name="currentUnitPrice"
                    control={control}
                    render={({
                      field: { ref, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <NumberInput
                        fullWidth
                        {...rest}
                        currencyStartAdornment
                        onChange={(value) => {
                          onChange(value);
                          handleOverallUnitPriceChange(
                            value ?? 0,
                            numberOfUnits,
                          );
                        }}
                        onFocus={() => setFieldFocus('unitPriceTcy')}
                        currency={holdingClassCurrency}
                        decimals={2}
                        maxDecimals={10}
                        fixedDecimalScale
                        disabled={viewOnly}
                        error={!!error}
                        helperText={error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormField>
                <FormField
                  size="md"
                  labelSize="xs"
                  label="Revaluation date"
                  layout="horizontal"
                >
                  <Controller
                    name="currentRevaluationDate"
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        fullWidth
                        value={new CalendarDay(value ?? new Date())}
                        onChange={(date) => {
                          onChange(date?.toUTCDate());
                          updateAllNewRevaluationsDate(
                            date?.toUTCDate() ?? new Date(),
                          );
                        }}
                        minDate={latestRevaluation?.ledgerDate}
                        error={!!error}
                        helperText={error?.message}
                        size="small"
                        disableFuture
                        disabled={viewOnly || disableDate}
                      />
                    )}
                  />
                </FormField>
              </InputsRow>
              <Divider />
              <CurrentValuationRow
                isNonLcy={isNonLcy}
                holdValueTcyLabel={
                  isNonLcy
                    ? `Total hold value (${getCurrencySymbol(
                        holdingClassCurrency,
                      )})`
                    : 'Total hold value'
                }
                holdValueLcyLabel={
                  isNonLcy && vehicleBaseCurrency
                    ? `Total hold value (${getCurrencySymbol(
                        vehicleBaseCurrency,
                      )})`
                    : undefined
                }
                currentValuation={{
                  currentHoldValue: currentHoldValueStaticFormatted,
                  currentHoldValueLcy: currentHoldValueLcyStaticFormatted,
                  averageUnitPriceTcy: averageUnitPriceStaticFormatted,
                  latestRevaluationDate: latestRevaluationDateFormatted,
                  currentNumberOfUnits: numberOfUnits,
                }}
                newValuation={{
                  holdValue: !assetHasMoreThanOneCurrency
                    ? numberToCurrencyString(newHoldValue, {
                        currency: holdingClassCurrency,
                      })
                    : '-',
                  holdValueLcy:
                    isNonLcy &&
                    vehicleBaseCurrency &&
                    newHoldValueLcy !== undefined
                      ? numberToCurrencyString(newHoldValueLcy, {
                          currency: vehicleBaseCurrency,
                        })
                      : undefined,
                  averageUnitPriceTcy: !assetHasMoreThanOneCurrency
                    ? numberToCurrencyString(newUnitPrice, {
                        currency: holdingClassCurrency,
                        unlimitedDp: true,
                      })
                    : '-',
                  numberOfUnits,
                  revaluationDate: newRevaluationDate
                    ? new CalendarDay(newRevaluationDate).toReadable()
                    : '-',
                }}
              />
            </Column>
          </Content>
        </CardComponent>
        {rateSelector}
        <Divider />
      </Column>
    </ErrorBoundary>
  );
};

export default OverallRevaluationSection;
